import {skipToken} from '@reduxjs/toolkit/query';

import {isNotNil} from 'ramda';

import {
  useCreateDataQueryQuery,
  useGetDocumentContextCountQuery,
  useGetGridTotalRowsQuery,
  useListCustomerMessagesQuery,
} from '@dms/api/core';
import {EntityResourceIds} from '@dms/api/shared';
import {useCustomNoticesBadge} from '@dms/features/custom-notices';
import {useTasksCount} from '@dms/features/tasks';
import {useChecklist, workshopSearchParams} from '@dms/shared';

import {Nullish} from 'shared/web';

import {useServiceCaseDetailBootstrap} from './useServiceCaseDetailBootstrap';

interface UseServiceCaseSectionBadgesProps {
  serviceCaseId: string;
  customerId: string | Nullish;
  activeSection: string;
}

export function useServiceCaseSectionBadges(props: UseServiceCaseSectionBadgesProps) {
  const {isBootstrapSettled} = useServiceCaseDetailBootstrap(props.serviceCaseId);
  // Only for sections whose badge query is also their content query, where fetching early is a
  // prefetch rather than an extra request. Badge-only counts stay behind the plain bootstrap gate.
  const canFetchSection = (section: string) =>
    isBootstrapSettled || props.activeSection === section;

  const {currentData: documentCount} = useGetDocumentContextCountQuery(
    {contextTarget: 'service-case', contextId: props.serviceCaseId},
    {skip: !isBootstrapSettled}
  );
  const {badgeSettings: checklistBadge} = useChecklist({
    recordId: canFetchSection(workshopSearchParams.serviceDetail.checklists)
      ? props.serviceCaseId
      : '',
    resourceId: 'SERVICE_CASE',
  });
  const {count: tasksTodoCount} = useTasksCount(
    {entityId: props.serviceCaseId, relation: EntityResourceIds.serviceCase, state: 'TODO'},
    {skip: !isBootstrapSettled}
  );
  const {count: tasksInProgressCount} = useTasksCount(
    {entityId: props.serviceCaseId, relation: EntityResourceIds.serviceCase, state: 'IN_PROGRESS'},
    {skip: !isBootstrapSettled}
  );
  const {customNoticesBadge} = useCustomNoticesBadge({
    resourceId: EntityResourceIds.serviceCase,
    recordId: canFetchSection(workshopSearchParams.serviceDetail.customNotices)
      ? props.serviceCaseId
      : undefined,
  });

  // The shared count hook has no skip option. Keep both requests behind the same gate here.
  const {currentData: activityQuery} = useCreateDataQueryQuery(
    {
      code: 'activity-log-service-case',
      createDataQueryRequestBody: {
        filters: {serviceCaseId: props.serviceCaseId},
        order: [],
        smartSearch: null,
      },
    },
    {skip: !isBootstrapSettled}
  );
  const {currentData: activityLogsCount} = useGetGridTotalRowsQuery(
    isBootstrapSettled && isNotNil(activityQuery?.dataQueryId)
      ? {code: 'activity-log-service-case', dataQueryId: activityQuery.dataQueryId}
      : skipToken
  );
  const {currentData: customerMessages} = useListCustomerMessagesQuery(
    canFetchSection(workshopSearchParams.serviceDetail.communication) && isNotNil(props.customerId)
      ? {
          customerId: props.customerId,
          recordId: props.serviceCaseId,
          resourceId: EntityResourceIds.serviceCase,
        }
      : skipToken
  );

  return {
    documentCount,
    checklistBadge,
    tasksCountBadge: tasksTodoCount + tasksInProgressCount,
    customNoticesBadge,
    activityLogsCount,
    outgoingMessagesCount:
      customerMessages?.messages.filter((message) => message.direction === 'OUTGOING').length ?? 0,
  };
}
